import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { baseChart, tightGrid } from '../ChartFrame'
import { LiveChartFrame } from './LiveChartFrame'
import { CardShell } from './CardShell'
import { DailyResponse, DayType, Resource } from './types'
import { ChartKind } from './sections'
import { SERIES, ChartTheme, themed, useChartTheme } from './chartTheme'
import { tooltipHtml } from './tooltip'
import { UNIT, compact } from './format'
import type { Fmt } from './format'
import { T, useFormat, useT } from './i18n'
import { apexLocale } from './chartLocale'

/** More points than this (about three years of days) are shown as weekly averages. */
const MAX_DAILY_POINTS = 1100

type Point = { x: number; y: number }

const utc = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

const DAY_TYPE_ID = {
  weekday: 'PROFILES_DASHBOARD.DAYTYPE.WEEKDAY',
  weekend: 'PROFILES_DASHBOARD.DAYTYPE.WEEKEND',
  event: 'PROFILES_DASHBOARD.DAYTYPE.EVENT',
} as const

export const dayTypeLabel = (t: T, type: DayType) => t(DAY_TYPE_ID[type])

/** Mean of the complete days per ISO week (week starts Monday); the point sits on the Monday. */
export function weeklyAverages(series: DailyResponse['series']): Point[] {
  const weeks = new Map<number, number[]>()
  series.forEach((row) => {
    if (!row.complete) return
    const time = utc(row.date)
    const weekday = (new Date(time).getUTCDay() + 6) % 7 // Monday = 0
    const monday = time - weekday * 86400000
    weeks.set(monday, [...(weeks.get(monday) || []), row.total])
  })
  return Array.from(weeks.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([x, values]) => ({
      x,
      y: Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 100) / 100,
    }))
}

/** Builds the hover card of the daily chart: date, weekday, consumption, day type, vs the average. */
export function dailyTooltip(
  theme: ChartTheme,
  t: T,
  f: Fmt,
  x: number,
  lookup: Map<number, DailyResponse['series'][number]>,
  average: number | null,
  weekly: boolean
): string {
  const iso = new Date(x).toISOString().slice(0, 10)
  if (weekly) {
    return tooltipHtml(theme, t('PROFILES_DASHBOARD.TIP.WEEK_OF', { date: f.date(iso) }), [
      {
        label: t('PROFILES_DASHBOARD.DAILY.SERIES_WEEKLY'),
        value: f.kwh(lookup.get(x)?.total),
        color: SERIES.green,
      },
    ])
  }
  const row = lookup.get(x)
  if (!row) return ''
  const title = `${f.weekdayOf(iso)}, ${f.date(iso)}`
  if (!row.complete) {
    return tooltipHtml(
      theme,
      title,
      [
        {
          label: t('PROFILES_DASHBOARD.TIP.REPORTED'),
          value: f.kwh(row.total),
          color: SERIES.yellow,
        },
      ],
      t('PROFILES_DASHBOARD.TIP.PARTIAL_NOTE')
    )
  }
  const rows = [
    {
      label: t('PROFILES_DASHBOARD.TIP.CONSUMPTION'),
      value: f.kwh(row.total),
      color: SERIES.green,
    },
    { label: t('PROFILES_DASHBOARD.TIP.DAY_TYPE'), value: dayTypeLabel(t, row.type) },
  ]
  if (average) {
    rows.push({
      label: t('PROFILES_DASHBOARD.TIP.VS_AVERAGE'),
      value: f.percent(((row.total - average) / average) * 100),
    } as (typeof rows)[number])
  }
  return tooltipHtml(theme, title, rows)
}

type Props = {
  daily: Resource<DailyResponse>
  chartType: ChartKind
}

export function DailyConsumptionCard({ daily, chartType }: Props) {
  const t = useT()
  const f = useFormat()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.SECTION.TIMESERIES')}
      subtitle={
        daily.status === 'ready'
          ? t('PROFILES_DASHBOARD.DAILY.SUBTITLE', {
              site: daily.data.meta.site.name,
              meterType:
                daily.data.meta.meter_type === 'Gas'
                  ? t('PROFILES_DASHBOARD.METER.GAS')
                  : t('PROFILES_DASHBOARD.METER.ELECTRICITY'),
            })
          : undefined
      }
      state={daily}
      empty={(d) =>
        d.series.length
          ? null
          : d.meta.available_from
          ? t('PROFILES_DASHBOARD.DAILY.EMPTY_RANGE', {
              from: f.date(d.meta.available_from),
              to: f.date(d.meta.available_to),
            })
          : t('PROFILES_DASHBOARD.DAILY.EMPTY_NONE')
      }
      render={(d) => <Chart data={d} chartType={chartType} />}
    />
  )
}

function Chart({ data, chartType }: { data: DailyResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const weekly = data.series.length > MAX_DAILY_POINTS
  const { main, partial } = useMemo(() => {
    if (weekly) return { main: weeklyAverages(data.series), partial: [] as Point[] }
    return {
      main: data.series.filter((r) => r.complete).map((r) => ({ x: utc(r.date), y: r.total })),
      partial: data.series.filter((r) => !r.complete).map((r) => ({ x: utc(r.date), y: r.total })),
    }
  }, [data.series, weekly])

  const lookup = useMemo(() => {
    const map = new Map<number, DailyResponse['series'][number]>()
    if (weekly) {
      weeklyAverages(data.series).forEach((p) =>
        map.set(p.x, { date: '', total: p.y, complete: true, type: 'weekday' })
      )
    } else {
      data.series.forEach((r) => map.set(utc(r.date), r))
    }
    return map
  }, [data.series, weekly])

  const options: ApexOptions = useMemo(() => {
    const { highest, lowest } = data.extremes
    // labels are centred on their point: shift them inward near the chart edges so they stay visible
    const xs = main.map((p) => p.x)
    const first = xs.length ? xs[0] : 0
    const last = xs.length ? xs[xs.length - 1] : 0
    const offsetX = (x: number) => {
      const fraction = last > first ? (x - first) / (last - first) : 0.5
      return fraction > 0.7 ? -90 : fraction < 0.3 ? 90 : 0
    }
    const mark = (
      point: { date: string; total: number } | null,
      kind: 'HIGHEST' | 'LOWEST',
      color: string
    ) =>
      point
        ? [
            {
              x: utc(point.date),
              y: point.total,
              marker: { size: 6, fillColor: color, strokeColor: '#fff', strokeWidth: 2 },
              label: {
                text: t(`PROFILES_DASHBOARD.DAILY.${kind}_LABEL`, {
                  value: f.kwh(point.total),
                  date: f.date(point.date),
                }),
                borderColor: color,
                offsetX: offsetX(utc(point.date)),
                offsetY: kind === 'LOWEST' ? 28 : -4,
                style: { color: '#fff', background: color, fontSize: '11px' },
              },
            },
          ]
        : []
    const average = data.kpis.average_per_day
    const opts: ApexOptions = {
      // One series and no per-series types: ApexCharts treats mixed (combo) charts as "hover the exact
      // point" charts, which made the tooltip impossible to trigger.
      chart: {
        ...baseChart,
        type: chartType,
        toolbar: { show: true },
        zoom: { enabled: true },
        ...apexLocale(t, f),
      },
      grid: tightGrid,
      stroke: { curve: 'straight', width: chartType === 'bar' ? 0 : 2.5 },
      dataLabels: { enabled: false },
      // size must be the plain number 0 (not an array): only then does ApexCharts create the
      // hover dot and move the crosshair and tooltip with the pointer
      markers: { size: 0, hover: { size: 6, sizeOffset: 2 } },
      xaxis: {
        type: 'datetime',
        labels: { datetimeUTC: true },
        crosshairs: { show: true, stroke: { dashArray: 3, width: 1 } },
        tooltip: { enabled: false },
      },
      yaxis: {
        title: {
          text: t(
            weekly ? 'PROFILES_DASHBOARD.DAILY.Y_AXIS_WEEKLY' : 'PROFILES_DASHBOARD.DAILY.Y_AXIS',
            { unit: UNIT }
          ),
        },
        labels: { formatter: compact },
        min: 0,
      },
      colors: [SERIES.green],
      legend: { show: false },
      annotations: {
        points: [
          ...partial.map((p) => ({
            x: p.x,
            y: p.y,
            marker: {
              size: 4,
              fillColor: SERIES.yellow,
              strokeColor: theme.surface,
              strokeWidth: 1,
            },
          })),
          ...mark(highest, 'HIGHEST', SERIES.red),
          ...mark(lowest, 'LOWEST', SERIES.purple),
        ],
      },
      tooltip: {
        // hovering anywhere along the chart shows the nearest day, no need to hit the line exactly
        shared: false,
        intersect: false,
        followCursor: false,
        custom: ({ seriesIndex, dataPointIndex, w }: any) => {
          const x = w.globals.seriesX?.[seriesIndex]?.[dataPointIndex]
          return dailyTooltip(theme, t, f, x, lookup, average, weekly)
        },
      },
    }
    if (chartType === 'area') {
      opts.fill = { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } }
    }
    return themed(opts, theme)
  }, [
    data.extremes,
    data.kpis.average_per_day,
    chartType,
    weekly,
    main,
    partial,
    lookup,
    theme,
    t,
    f,
  ])

  const series = useMemo(
    () => [
      {
        name: weekly
          ? t('PROFILES_DASHBOARD.DAILY.SERIES_WEEKLY')
          : t('PROFILES_DASHBOARD.SECTION.TIMESERIES'),
        data: main,
      },
    ],
    [main, weekly, t]
  )

  return (
    <>
      {weekly ? (
        <div className='fs-8 text-muted mb-2'>{t('PROFILES_DASHBOARD.DAILY.WEEKLY_NOTE')}</div>
      ) : null}
      <LiveChartFrame>
        <ReactApexChart
          key={`${data.meta.site.id}-${data.meta.meter_type}-${data.meta.start}-${data.meta.end}-${chartType}-${theme.mode}-${f.locale}`}
          options={options}
          series={series as ApexAxisChartSeries}
          type={chartType}
          width='100%'
          height={380}
        />
      </LiveChartFrame>
      {partial.length ? (
        <div className='fs-8 text-muted'>
          <span
            className='d-inline-block rounded-circle me-2 align-middle'
            style={{ width: 8, height: 8, background: SERIES.yellow }}
          />
          {t('PROFILES_DASHBOARD.DAILY.PARTIAL_NOTE', { count: partial.length })}
        </div>
      ) : null}
    </>
  )
}
